<template>
  <div
    class="lang-string-editor"
    v-if="value"
    :class="{ 'lang-string-multi': multiline }"
    ref="langStringEditorBase"
  >
    <div v-if="mode === 'ja' || expand">
      <textarea
        class="jps-textarea"
        v-if="multiline"
        :placeholder="placeholder"
        rows="3"
        v-model="value.ja"
        @keydown.tab="tab(0, $event, true)"
      ></textarea>
      <input
        :class="inputClass"
        v-else
        type="text"
        :placeholder="placeholder"
        v-model="value.ja"
        @keydown.tab="tab(0, $event)"
      />
    </div>
    <div v-if="mode === 'en' || expand">
      <textarea
        class="jps-textarea"
        v-if="multiline"
        :placeholder="placeholder"
        rows="3"
        v-model="value.en"
        @keydown.tab="tab(1, $event, true)"
      ></textarea>
      <input
        :class="inputClass"
        v-else
        type="text"
        :placeholder="placeholder"
        v-model="value.en"
        @keydown.tab="tab(1, $event)"
      />
    </div>
    <div v-if="yomi && (mode === 'yomi' || expand)">
      <textarea
        class="jps-textarea"
        v-if="multiline"
        :placeholder="placeholder"
        v-model="value.yomi"
        @keydown.tab="tab(2, $event, true)"
      ></textarea>
      <input
        :class="inputClass"
        v-else
        type="text"
        :placeholder="placeholder"
        v-model="value.yomi"
        @keydown.tab="tab(2, $event)"
      />
    </div>
    <div
      class="editor-lang is-hidden-mobile"
      :class="{ multi: multiline }"
      v-if="!expand && !noLang"
    >
      <UiIconRadioButton
        class="input-lang"
        :class="{ 'no-input': !value.ja }"
        v-model="mode"
        value="ja"
        id="lang-string-editor-ja"
        arialabel="ja"
        tabindex="-1">{{ lss('日本語', 'Jpn') }}</UiIconRadioButton>
      <UiIconRadioButton
        class="input-lang"
        :class="{ 'no-input': !value.en }"
        v-model="mode"
        value="en"
        id="lang-string-editor-en"
        arialabel="en"
        tabindex="-1">{{ lss('英語', 'Eng') }}</UiIconRadioButton>
      <UiIconRadioButton
        v-if="yomi"
        class="input-lang"
        :class="{ 'no-input': !value.yomi }"
        v-model="mode"
        value="yomi"
        id="lang-string-editor-yomi"
        arialabel="yomi"
        tabindex="-1">{{ lss('読み', 'Reading') }}</UiIconRadioButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
const { ls, lss, isJa } = useI18n()

interface Props {
  expand?: boolean,
  inputClass?: string,
  noLang?: boolean,
  noPlaceholder?: boolean,
  value?: LangStringYomi,
  rule?: ValidationRuleField
}
const props = withDefaults(defineProps<Props>(),{
  expand: false,
  inputClass: 'jps-input'
})

const langStringEditorBase = ref<HTMLElement>(null)

type ModeType = 'ja' | 'en' | 'yomi'
const mode: Ref<ModeType> = ref('ja')
onBeforeMount(() => {
  if (props.value) {
    if (!props.value.ja) props.value.ja = ''
    if (!props.value.en) props.value.en = ''
    if (yomi.value && !props.value.yomi) props.value.yomi = ''
  }
})

watch(
  () => isJa,
  (newVal) => {
    if (!props.noLang) {
      mode.value = newVal ? 'ja' : 'en'
    }
  },
)

const tab = (i: number, evt: KeyboardEvent, area = false) => {
  if (props.noLang) return
  if (i == 0) {
    if (evt.shiftKey) {
      //
    } else {
      mode.value = 'en'
      evt.preventDefault()
      nextTick(() => {
        langStringEditorBase.value.getElementsByTagName(area ? 'textarea' : 'input')[0].focus()
      })
    }
  } else if (i == 1) {
    if (evt.shiftKey) {
      mode.value = 'ja'
      evt.preventDefault()
      nextTick(() => {
        langStringEditorBase.value.getElementsByTagName(area ? 'textarea' : 'input')[0].focus()
      })
    } else {
      if (yomi.value) {
        mode.value = 'yomi'
        evt.preventDefault()
        nextTick(() => {
          langStringEditorBase.value.getElementsByTagName(area ? 'textarea' : 'input')[0].focus()
        })
      }
    }
  } else if (i == 2) {
    if (evt.shiftKey) {
      mode.value = 'en'
      evt.preventDefault()
      nextTick(() => {
        langStringEditorBase.value.getElementsByTagName('input')[0].focus()
      })
    } else {
      //
    }
  }
}

const placeholder = computed(() => {
  if (props.noPlaceholder) return ''
  return ls(props.rule.label)
})

const yomi = computed(() => {
  return props.rule.objRuleName === 'jps-lang_string_yomi'
})

const multiline = computed(() => {
  return props.rule.allowMultiline
})

const liconClass = (num: number) => {
  let clazz = ''
  // if (top.value == num || props.expand) clazz += 'ls-selected'
  if (
    (num == 0 &&
      props.rule.jaMandatory &&
      (props.value.ja === undefined || props.value.ja === '')) ||
    (num == 1 &&
      props.rule.enMandatory &&
      (props.value.en === undefined || props.value.en === '')) ||
    (num == 2 &&
      props.rule.yomiMandatory &&
      (props.value.yomi === undefined || props.value.yomi === ''))
  )
    clazz += ' ls-invalid'
  return clazz
}
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps {
  .lang-string-editor {
    position: relative;
    input {
      z-index: 1;
      width: 100%;
      padding-right: 6rem;
      display: block;
    }

    &.lse-center {
      input {
        text-align: center;
        padding-right: 0;
      }
    }
    textarea {
      display: block;
      width: 100%;
      border: 1px solid $border-input;
      border-radius: $global-radius;
    }
    .editor-lang {
      position: absolute;
      background-color: rgba($white, 0.8);
      display: flex;
      padding: 6px 4px;
      bottom: 1px;
      right: 12px;
      z-index: 2;
      align-items: flex-end;
      line-height: 1 !important;

      .input-lang {
        @include no-decorative-button;
        padding-right: 3px;
        font-size: 0.75rem !important;
        font-weight: bold;
      }
    }
  }
}
</style>
